import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

export default function BottomTabBar() {
  const { user } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;

  const isLanding = pathname === "/" || pathname.startsWith("/landing");
  const isResetPassword = pathname === "/reset-password";
  const isAdmin = pathname.startsWith("/admin");

  const [unreadChats, setUnreadChats] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  const totalUnread = unreadChats + unreadNotifs;

  // Ungelesene Chats
  useEffect(() => {
    if (!user?.id) {
      setUnreadChats(0);
      return;
    }

    const fetchUnread = async () => {
      const { count, error } = await supabase
        .from("chat_read_status")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("read", false);

      if (!error) setUnreadChats(count || 0);
    };

    fetchUnread();
  }, [user?.id, pathname]);

  // Ungelesene Benachrichtigungen (mit Realtime)
  useEffect(() => {
    if (!user?.id) {
      setUnreadNotifs(0);
      return;
    }

    const fetchNotifs = async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id")
        .not("read_by", "cs", `{${user.id}}`)
        .neq("user_id", user.id);

      if (!error && data) setUnreadNotifs(data.length);
    };

    fetchNotifs();

    const channel = supabase
      .channel("bottom-tab-notif-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications" },
        fetchNotifs
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  if (!user || isLanding || isResetPassword || isAdmin) return null;

  const isHome = pathname === "/dashboard";
  const isTrips = pathname.startsWith("/trip") || pathname === "/trips";
  const isMessages =
    pathname === "/chats" ||
    pathname.startsWith("/chat/") ||
    pathname === "/notifications";
  const isProfile = pathname === "/profile";

  return (
    <nav className="fixed bottom-0 inset-x-0 z-50 bg-[#161b27] border-t border-[#2a3347] flex items-center justify-around h-16">
      {/* 🏠 Home */}
      <Link
        to="/dashboard"
        className={`flex flex-col items-center py-1 px-3 transition-colors ${
          isHome ? "text-sky-400" : "text-slate-500 hover:text-slate-300"
        }`}
      >
        <span className="text-xl">🏠</span>
        <span className="text-[10px] mt-0.5">Home</span>
      </Link>

      {/* ✈️ Reisen */}
      <Link
        to="/trips"
        className={`flex flex-col items-center py-1 px-3 transition-colors ${
          isTrips && !isHome
            ? "text-sky-400"
            : "text-slate-500 hover:text-slate-300"
        }`}
      >
        <span className="text-xl">✈️</span>
        <span className="text-[10px] mt-0.5">Reisen</span>
      </Link>

      {/* ＋ Neu – primäre CTA */}
      {/*
      -- Temporär ausgeschaltet -- FAB Plus Button auf Startseite
      <Link
        to="/dashboard?create=true"
        className="flex flex-col items-center justify-center bg-sky-500 hover:bg-sky-600 rounded-full w-14 h-14 -mt-5 shadow-lg shadow-sky-500/30 transition-colors"
        aria-label="Neue Reise erstellen"
      >
        <span className="text-white text-2xl leading-none font-light">＋</span>
      </Link>
      */}
      {/* 💬 Nachrichten */}
      <Link
        to="/chats"
        className={`relative flex flex-col items-center py-1 px-3 transition-colors ${
          isMessages ? "text-sky-400" : "text-slate-500 hover:text-slate-300"
        }`}
      >
        <span className="relative text-xl">
          💬
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-2 bg-sky-500 text-white text-[9px] min-w-[16px] h-[16px] px-0.5 rounded-full flex items-center justify-center border border-[#0d1117] leading-none">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </span>
        <span className="text-[10px] mt-0.5">Nachrichten</span>
      </Link>

      {/* 👤 Profil */}
      <Link
        to="/profile"
        className={`flex flex-col items-center py-1 px-3 transition-colors ${
          isProfile ? "text-sky-400" : "text-slate-500 hover:text-slate-300"
        }`}
      >
        <span className="text-xl">👤</span>
        <span className="text-[10px] mt-0.5">Profil</span>
      </Link>
    </nav>
  );
}
